import React from 'react';

import { cssMap } from '@compiled/react';

import { cx } from '@atlaskit/css';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import {
	Card,
	type ElementItem,
	ElementName,
	SmartLinkPosition,
	SmartLinkSize,
	SmartLinkTheme,
	TitleBlock,
} from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';

import { type LinkListVariantProps } from '../types';

const styles = cssMap({
	root: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
	},
	linkWrapper: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="smart-block-title-resolved-view"]': {
			display: 'flex',
			alignItems: 'center',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& a[data-smart-element="Title"]': {
			fontWeight: token('font.weight.medium'),
			color: token('color.text'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="smart-element-group"]': {
			maxWidth: '375px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="smart-block-title-resolved-view"] .actions-button-group': {
			marginLeft: 'auto',
			alignSelf: 'center',
			display: 'flex',
			alignItems: 'center',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'span[data-smart-element="ModifiedOn"]::before': {
			content: 'none',
			display: 'none',
		},
	},
	expandWrapper: {
		height: '40px',
	},
	borderTopRounded: {
		borderTopLeftRadius: token('radius.medium'),
		borderTopRightRadius: token('radius.medium'),
	},
	borderBottomRounded: {
		borderBottomLeftRadius: token('radius.medium'),
		borderBottomRightRadius: token('radius.medium'),
	},
});

const metadata: ElementItem[] = [{ name: ElementName.State }];

export const LinkListCompact = ({ visibleLinks, canExpand }: LinkListVariantProps) => {
	return (
		<Stack space="space.0">
			{visibleLinks.map((url, idx) => (
				<Box
					xcss={cx(
						styles.root,
						styles.linkWrapper,
						idx === 0 && styles.borderTopRounded,
						idx === visibleLinks.length - 1 && !canExpand && styles.borderBottomRounded,
					)}
					key={`${url}=${idx}`}
					testId="link-list-compact-item"
				>
					<Card
						appearance="block"
						url={url}
						ui={{
							theme: SmartLinkTheme.Black,
							clickableContainer: true,
							hideBackground: true,
							hideElevation: true,
							hidePadding: true,
							size: SmartLinkSize.Large,
							removeBlockRestriction: true,
						}}
					>
						<TitleBlock
							maxLines={1}
							showActionOnHover
							metadata={metadata}
							subtitle={[
								{ name: ElementName.Provider, hideIcon: true },
								{ name: ElementName.CreatedBy },
							]}
							position={SmartLinkPosition.Center}
						/>
					</Card>
				</Box>
			))}
		</Stack>
	);
};
